import React, { useEffect, useState } from 'react';

import { useTheme } from 'contexts/ThemeContext';
import { useUser } from 'contexts/UserContext';

import { granularities, kpis } from 'utils/constants/metrics';
import { ERMES_MODULE_OPTIONS } from 'utils/constants/ermesModules';
import { USERPROFILE } from 'utils/rest';

import { HelpTooltip, Text } from 'components/DataDisplay';
import { Modal } from 'components/Feedback';
import { FormSection } from 'components/FormSection';
import {
  Button,
  Checkbox,
  MultipleSelect,
  Select,
  TextField
} from 'components/Inputs';
import { CustomSelect } from 'components/Inputs/CustomSelectSearch';
import { Container, Item, Row } from 'components/Layout';

import {
  featuresMapping,
  featuresMappingContent,
  funnelSectionMapping,
  pmlSectionMapping,
  simplifiedBoFeaturesMapping
} from './mappings';

const ErrorState = {
  nameError: false,
  keyError: false,
  viewKeyError: false
};

const BO_ACCESS_OPTIONS = [
  { value: 'classic', label: 'BO classique' },
  { value: 'simplified', label: 'BO simplifié' },
  { value: 'none', label: 'Aucun' }
];

export const ModalForm = props => {
  const {
    user: { domain }
  } = useUser();
  const { config, onValidate, onClose, actionName, views, metrics } = props;
  const { themeColors } = useTheme();

  const [profileOptions, setProfileOptions] = useState([]);

  const initialState = {
    name: config?.name || '',
    key: config?.key || '',
    description: config?.description,
    viewKey: config?.viewKey,
    features: config?.features || {},
    showInSimplified: config?.showInSimplified ?? false,
    funnelKpis: config?.funnelKpis || [],
    defaultFunnelKpis: config?.defaultFunnelKpis || [],
    funnelGranularities: config?.funnelGranularities || [],
    defaultFunnelGranularities: config?.defaultFunnelGranularities || [],
    funnelExportTemporalities: config?.funnelExportTemporalities || [],
    modules: config?.modules || []
  };
  const [
    {
      name,
      key,
      description,
      features,
      viewKey,
      showInSimplified,
      funnelKpis,
      defaultFunnelKpis,
      funnelGranularities,
      defaultFunnelGranularities,
      funnelExportTemporalities,
      modules
    },
    setState
  ] = useState(initialState);

  const [{ nameError, keyError, viewKeyError }, setError] = useState(
    ErrorState
  );

  const handleChange = field => value => {
    let newValue = value;
    if (typeof value === 'string') {
      newValue = value.trim();
    }
    setState(prevState => {
      return { ...prevState, [field]: newValue };
    });
  };

  const resetStates = () => {
    setState(initialState);
    setError(ErrorState);
  };

  const toggleError = (itemKey, state) => {
    setError(prevState => {
      const errors = { ...prevState };
      errors[`${itemKey}Error`] = state;
      return errors;
    });
  };

  const handleModulesChange = newModules => {
    let updated = [...newModules];

    const activationAdded =
      updated.includes('activation') && !modules.includes('activation');
    const audiencesRemoved =
      !updated.includes('audiences') && modules.includes('audiences');

    if (activationAdded && !updated.includes('audiences')) {
      updated.push('audiences');
    }

    if (audiencesRemoved) {
      updated = updated.filter(m => m !== 'activation');
    }

    handleChange('modules')(updated);
  };

  const getBoAccessValue = () => {
    if (features.isSimplified) {
      return 'simplified';
    }

    if (features.canAccessBO) {
      return 'classic';
    }

    return 'none';
  };

  const doAction = () => {
    let error = false;

    if (!name || name.trim().length === 0) {
      toggleError('name', true);
      error = true;
    }

    if (!key || key?.trim().length === 0) {
      toggleError('key', true);
      error = true;
    }
    if (!viewKey || viewKey?.trim().length === 0) {
      toggleError('viewKey', true);
      error = true;
    }

    const boAccessValue = getBoAccessValue();
    const normalizedFeatures = {
      ...features
    };

    if (boAccessValue !== 'simplified') {
      normalizedFeatures.customSetupEnabled = false;
      normalizedFeatures.pointOfSalesBoSimplified = false;
      normalizedFeatures.profilesAccess = [];
    }

    const datas = {
      name,
      key,
      description,
      showInSimplified,
      features: normalizedFeatures,
      viewKey,
      funnelKpis,
      defaultFunnelKpis: defaultFunnelKpis.filter(el => {
        if (funnelKpis.length > 0) {
          return funnelKpis.includes(el);
        }
        return true;
      }),
      funnelGranularities,
      defaultFunnelGranularities: defaultFunnelGranularities.filter(el => {
        if (funnelGranularities.length > 0) {
          return funnelGranularities.includes(el);
        }
        return true;
      }),
      funnelExportTemporalities,
      ...(modules?.length ? { modules } : config ? { modules: null } : {})
    };
    if (!error) {
      onValidate(datas);
      resetStates();
    }
  };

  const getActions = () => {
    return (
      <Container>
        <Row spacing={1}>
          <Item xs justify="flex-end">
            <Button
              variant="contained"
              color={themeColors.global}
              size="medium"
              widthSize="medium"
              onClick={doAction}
            >
              {actionName} le profil
            </Button>
          </Item>
        </Row>
      </Container>
    );
  };

  const disableFields = !!config;

  const getCheckbox = item => {
    return (
      <Item xs={6} justify="flex-start">
        <Row spacing={0} justify="flex-start">
          <Item xs={11} justify="flex-start">
            <Checkbox
              onChange={event => {
                handleChange('features')({
                  ...features,
                  [item.key]: event.target.checked
                });
              }}
              checked={features[item.key]}
            />
            <Text>{item.label}</Text>
            {item.tooltip && (
              <HelpTooltip
                title={item.tooltip}
                style={{
                  paddingLeft: '10px'
                }}
              />
            )}
          </Item>
        </Row>
      </Item>
    );
  };

  useEffect(() => {
    const fetchDomainProfiles = async () => {
      const profiles = await USERPROFILE.get();
      setProfileOptions(
        profiles.map(profile => ({
          key: profile.key,
          label: profile.name
        }))
      );
    };

    fetchDomainProfiles();
  }, [domain]);

  const temporalityOptions = [
    { key: 'day', label: 'Jour' },
    { key: 'week', label: 'Semaine' },
    { key: 'month', label: 'Mois' },
    { key: 'year', label: 'Année' },
    { key: 'global', label: 'Global' }
  ];

  return (
    <Modal
      actions={getActions()}
      {...props}
      onClose={() => {
        resetStates();
        onClose();
      }}
    >
      <Container>
        <Row spacing={1}>
          <Item justify="flex-start">
            <FormSection title="Général" xs={3}>
              <Container>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={11} justify="flex-start">
                        <Text color={nameError ? 'error' : 'inherit'}>
                          Nom du profil *
                        </Text>
                        <TextField
                          label=""
                          error={nameError}
                          onChange={handleChange('name')}
                          value={name}
                        />
                      </Item>
                    </Row>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={11} justify="flex-start">
                        <Text color={keyError ? 'error' : 'inherit'}>
                          Key du profil *
                        </Text>
                        <TextField
                          label=""
                          error={keyError}
                          onChange={handleChange('key')}
                          value={key}
                          disabled={disableFields}
                        />
                      </Item>
                    </Row>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={11} justify="flex-start">
                        <Text color={viewKeyError ? 'error' : 'inherit'}>
                          Vue *
                        </Text>
                        <Select
                          disabled={disableFields}
                          label=""
                          error={viewKeyError}
                          options={views}
                          onChange={handleChange('viewKey')}
                          value={viewKey}
                        />
                      </Item>
                    </Row>
                  </Item>
                  <Item xs={12} justify="flex-start">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={11} justify="flex-start">
                        <Text>Description</Text>
                        <TextField
                          label=""
                          onChange={handleChange('description')}
                          value={description}
                          multiline
                          rows={3}
                        />
                      </Item>
                    </Row>
                  </Item>
                </Row>
              </Container>
            </FormSection>
            <FormSection title="Droits accordés au profil" xs={3}>
              <Container>
                <Row spacing={0} justify="flex-start">
                  {featuresMapping.map(item => getCheckbox(item))}
                </Row>
              </Container>
            </FormSection>
            <FormSection title="Modules Ermes" xs={3}>
              <Container>
                <Row spacing={0} justify="flex-start">
                  <Item xs={12} justify="flex-start">
                    <Text>
                      Les modules accessibles à l'utilisateur correspondent à
                      l'intersection entre les droits du profil et ceux du
                      domaine actif.
                    </Text>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <MultipleSelect
                      title="Accès aux modules"
                      placeHolder="Tous"
                      selectAll
                      onChange={options => {
                        handleModulesChange(options.map(el => el.key));
                      }}
                      options={ERMES_MODULE_OPTIONS}
                      selected={modules}
                      small
                    />
                  </Item>
                </Row>
              </Container>
            </FormSection>
            <FormSection title="Droits accordés pour le back-office" xs={3}>
              <Container>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={11} justify="flex-start">
                        <Text>Accès au back-office</Text>
                        <Select
                          label=""
                          options={BO_ACCESS_OPTIONS}
                          onChange={value => {
                            handleChange('features')({
                              ...features,
                              canAccessBO: value === 'classic',
                              isSimplified: value === 'simplified'
                            });
                          }}
                          value={getBoAccessValue()}
                        />
                      </Item>
                    </Row>
                  </Item>
                </Row>

                {getBoAccessValue() === 'simplified' && (
                  <>
                    <Row spacing={0} justify="flex-start">
                      <Item xs={6} justify="flex-start">
                        <Row spacing={0} justify="flex-start">
                          <Item xs={11} justify="flex-start">
                            <CustomSelect
                              title="Accès aux profils"
                              tooltip="Les utilisateurs pourront voir, créer, modifier et supprimer les utilisateurs ayant ces profils via le BO simplifié."
                              onChange={options => {
                                handleChange('features')({
                                  ...features,
                                  profilesAccess: options.map(
                                    option => option.key
                                  )
                                });
                              }}
                              options={profileOptions}
                              placeholder="Sélectionner des profils"
                              selected={features.profilesAccess || []}
                              small
                              multiple
                              searchBar
                              width="382px"
                              hideNone
                            />
                          </Item>
                        </Row>
                      </Item>
                    </Row>
                    <Row spacing={0} justify="flex-start">
                      {simplifiedBoFeaturesMapping.map(item =>
                        getCheckbox(item)
                      )}
                    </Row>
                  </>
                )}
              </Container>
            </FormSection>
            <FormSection title="Droits accordés au Module Contenus" xs={3}>
              <Container>
                <Row spacing={0} justify="flex-start">
                  {featuresMappingContent.map(item => getCheckbox(item))}
                </Row>
              </Container>
            </FormSection>
            {viewKey === 'pml' && (
              <FormSection
                title="Sections affichées dans le dashboard PML"
                xs={3}
              >
                <Container>
                  <Row spacing={0} justify="flex-start">
                    {pmlSectionMapping.map(item => getCheckbox(item))}
                  </Row>
                </Container>
              </FormSection>
            )}
            <FormSection
              title="Affichage funnel (performance médias / PML)"
              xs={3}
            >
              <Container>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={11} justify="flex-start">
                        <MultipleSelect
                          title="KPIs séléctionnables"
                          placeHolder="Tous les KPIs"
                          selectAll
                          onChange={options => {
                            handleChange('funnelKpis')(
                              options.map(el => el.key)
                            );
                          }}
                          options={
                            metrics?.kpis?.map(el => {
                              return { key: el, label: kpis[el] || el };
                            }) || []
                          }
                          selected={funnelKpis}
                          small
                        />
                      </Item>
                    </Row>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={11} justify="flex-start">
                        <MultipleSelect
                          title="KPIs affichés"
                          placeHolder="Par défaut"
                          selectAll
                          onChange={options => {
                            handleChange('defaultFunnelKpis')(
                              options.map(el => el.key)
                            );
                          }}
                          options={(funnelKpis.length !== 0
                            ? funnelKpis
                            : metrics?.kpis
                          )?.map(el => {
                            return { key: el, label: kpis[el] || el };
                          })}
                          selected={defaultFunnelKpis}
                          small
                        />
                      </Item>
                    </Row>
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={11} justify="flex-start">
                        <MultipleSelect
                          title="Granularités séléctionnables"
                          placeHolder="Toutes les granularités"
                          onChange={options => {
                            handleChange('funnelGranularities')(
                              options.map(el => el.key)
                            );
                          }}
                          options={
                            metrics?.granularities.map(el => {
                              return {
                                key: el,
                                label: granularities[el] || el
                              };
                            }) || []
                          }
                          selected={funnelGranularities}
                          selectAll
                          small
                        />
                      </Item>
                    </Row>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={11} justify="flex-start">
                        <MultipleSelect
                          title="Granularités affichées"
                          placeHolder="Par défaut"
                          onChange={options => {
                            handleChange('defaultFunnelGranularities')(
                              options.map(el => el.key)
                            );
                          }}
                          options={
                            (funnelGranularities.length > 0
                              ? funnelGranularities
                              : metrics?.granularities
                            ).map(el => {
                              return {
                                key: el,
                                label: granularities[el] || el
                              };
                            }) || []
                          }
                          selected={defaultFunnelGranularities}
                          small
                          selectAll
                        />
                      </Item>
                    </Row>
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={11} justify="flex-start">
                        <MultipleSelect
                          title="Temporalités séléctionnables export données"
                          placeHolder="Toutes les temporalités"
                          onChange={options => {
                            handleChange('funnelExportTemporalities')(
                              options.map(el => el.key)
                            );
                          }}
                          options={temporalityOptions}
                          selected={funnelExportTemporalities}
                          selectAll
                          small
                        />
                      </Item>
                    </Row>
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  {funnelSectionMapping.map(item => getCheckbox(item))}
                </Row>
              </Container>
            </FormSection>
          </Item>
        </Row>
      </Container>
    </Modal>
  );
};

export default ModalForm;
